import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import dayjs from '@/lib/dayjs'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useSuspenseQuery,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { useEffect, useEffectEvent } from 'react'
import {
  createPriceAdjustment,
  disablePriceAdjustment,
  deletePriceAdjustment,
  enablePriceAdjustment,
  getPriceAdjustment,
  getPriceAdjustments,
  getPriceAdjustmentTimeline,
  previewPriceAdjustment,
  resetActivePriceAdjustments,
  updatePriceAdjustment,
  type PriceAdjustmentIndexKeyEntries,
  type PriceAdjustmentOrganizationScope,
  type PriceAdjustmentScope,
  type PriceAdjustmentShowKeyEntries,
  type PriceAdjustmentTimelineKeyEntries,
} from './priceAdjustments.api'
import { invalidatePricingMutationDependencies } from './priceAdjustments.invalidation'
import { getPriceAdjustmentUpcomingStartTimestamp } from './priceAdjustments.summary'
import type {
  PriceAdjustmentDisableMode,
  PriceAdjustmentIdInput,
  PriceAdjustmentIndexFilters,
  PriceAdjustmentIndexResult,
  PriceAdjustmentInput,
  PriceAdjustmentPreview,
  PriceAdjustmentPreviewInput,
  PriceAdjustmentResult,
  SavedPriceAdjustmentResult,
} from './priceAdjustments.types'

/**
 * Hooks fall back to the route's organization; the `all` key without one matches every
 * organization's price changes; the scoped key builders throw without one.
 */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface PriceAdjustmentIndexScope extends OrganizationScope {
  filters?: PriceAdjustmentIndexFilters | undefined
}

/** The show key names one price change. */
interface PriceAdjustmentShowKeyScope extends OrganizationScope {
  priceAdjustmentId: PriceAdjustmentIdInput
}

/** Query options build requests, which need the organization. */
type PriceAdjustmentIndexQueryScope = PriceAdjustmentOrganizationScope & {
  filters?: PriceAdjustmentIndexFilters | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type PriceAdjustmentIndexQueryKey = [AdminQueryRoot & PriceAdjustmentIndexKeyEntries]
type PriceAdjustmentShowQueryKey = [AdminQueryRoot & PriceAdjustmentShowKeyEntries]
type PriceAdjustmentTimelineQueryKey = [AdminQueryRoot & PriceAdjustmentTimelineKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

type PriceAdjustmentPreviewQueryParams = QueryParams<
  UseQueryOptions<PriceAdjustmentPreview, Error, PriceAdjustmentPreview, AdminQueryKey>
>

export const priceAdjustmentFilters = {
  include: ['collections'],
} satisfies PriceAdjustmentIndexFilters

const MAX_SET_TIMEOUT_DELAY = 2_147_483_647
const triggeredPriceAdjustmentBoundariesByKey = new Map<string, string>()

/** The OneStem global organization has no price changes. */
export const supportsPriceAdjustmentsForOrganizationId = (organizationId: OrganizationIdInput) =>
  Number(organizationId) !== Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

export const getMillisecondsUntilPriceAdjustmentBoundary = (boundaryAt: string): number | null => {
  const boundary = (() => {
    try {
      return dayjs(boundaryAt)
    } catch {
      return null
    }
  })()

  if (!boundary?.isValid()) {
    return null
  }

  return Math.min(MAX_SET_TIMEOUT_DELAY, Math.max(0, boundary.diff(dayjs())))
}

const getPriceAdjustmentRefreshBoundaryAt = (
  data: PriceAdjustmentIndexResult | PriceAdjustmentResult | undefined
) => {
  const explicitBoundaryTimestamp = (() => {
    if (!data?.nextPriceAdjustmentChangeAt) {
      return null
    }

    try {
      const boundary = dayjs(data?.nextPriceAdjustmentChangeAt)
      return boundary.isValid() ? boundary.valueOf() : null
    } catch {
      return null
    }
  })()

  const fallbackBoundaryTimestamp =
    (Array.isArray(data) ? data : [data])
      .filter((adjustment) => adjustment?.status === 'scheduled' || adjustment?.status === 'draft')
      .map((adjustment) => getPriceAdjustmentUpcomingStartTimestamp(adjustment))
      .filter((timestamp) => timestamp !== null)
      .sort((left, right) => left - right)[0] ?? null

  const nextBoundaryTimestamp =
    [explicitBoundaryTimestamp, fallbackBoundaryTimestamp]
      .filter((timestamp) => timestamp !== null)
      .sort((left, right) => left - right)[0] ?? null

  return nextBoundaryTimestamp === null ? null : dayjs(nextBoundaryTimestamp).toISOString()
}

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return (
    (typeof value === 'object' || typeof value === 'function') &&
    value !== null &&
    'then' in value &&
    typeof value.then === 'function'
  )
}

interface PriceAdjustmentBoundaryCallbackOptions {
  boundaryAt: string
  /** A returned promise that rejects lets the next caller with the same dedupe key retry. */
  onBoundary: () => unknown
  /** Callers sharing a key fire a past-due boundary once. */
  dedupeKey?: string | null
}

/** Calls `onBoundary` when `boundaryAt` is reached; returns a function that cancels it. */
export const schedulePriceAdjustmentBoundaryCallback = ({
  boundaryAt,
  onBoundary,
  dedupeKey = null,
}: PriceAdjustmentBoundaryCallbackOptions) => {
  let timeoutId: number | null = null
  let cancelled = false
  let boundaryTriggered = false

  const fireBoundary = () => {
    if (cancelled || boundaryTriggered) {
      return
    }

    boundaryTriggered = true

    if (dedupeKey) {
      triggeredPriceAdjustmentBoundariesByKey.set(dedupeKey, boundaryAt)
    }

    const clearDedupeAfterFailure = () => {
      boundaryTriggered = false

      if (dedupeKey && triggeredPriceAdjustmentBoundariesByKey.get(dedupeKey) === boundaryAt) {
        triggeredPriceAdjustmentBoundariesByKey.delete(dedupeKey)
      }
    }

    try {
      const boundaryResult = onBoundary()

      if (isThenable(boundaryResult)) {
        void Promise.resolve(boundaryResult).catch(clearDedupeAfterFailure)
      }
    } catch {
      clearDedupeAfterFailure()
    }
  }

  const scheduleNextCallback = () => {
    const delay = getMillisecondsUntilPriceAdjustmentBoundary(boundaryAt)

    if (delay === null || cancelled) {
      return
    }

    if (delay === 0) {
      if (dedupeKey && triggeredPriceAdjustmentBoundariesByKey.get(dedupeKey) === boundaryAt) {
        return
      }

      fireBoundary()
      return
    }

    timeoutId = window.setTimeout(() => {
      timeoutId = null

      if (cancelled) {
        return
      }

      scheduleNextCallback()
    }, delay)
  }

  scheduleNextCallback()

  return () => {
    cancelled = true

    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
    }
  }
}

export const priceAdjustmentQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'price-adjustment' }, { organizationId }),
  ],
  index: ({ organizationId, filters }: PriceAdjustmentIndexScope): PriceAdjustmentIndexQueryKey =>
    buildScopedAdminQueryKey(priceAdjustmentQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  show: ({
    organizationId,
    priceAdjustmentId,
  }: PriceAdjustmentShowKeyScope): PriceAdjustmentShowQueryKey => {
    const [entries] = buildScopedAdminQueryKey(
      priceAdjustmentQueryKeys.all({ organizationId })[0],
      { organizationId, scope: 'show' }
    )

    // Set after the shared builder, whose loosely typed entries would make the id optional.
    return [{ ...entries, priceAdjustmentId }]
  },
  timeline: ({ organizationId }: OrganizationScope): PriceAdjustmentTimelineQueryKey =>
    buildScopedAdminQueryKey(priceAdjustmentQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'timeline',
    }),
  preview: ({
    organizationId,
    payload,
  }: OrganizationScope & { payload?: PriceAdjustmentPreviewInput | null }) =>
    buildScopedAdminQueryKey(priceAdjustmentQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'preview',
      payload,
    }),
}

export const priceAdjustmentQueries = {
  index: ({ organizationId, filters }: PriceAdjustmentIndexQueryScope) =>
    queryOptions({
      queryKey: priceAdjustmentQueryKeys.index({ organizationId, filters }),
      queryFn: getPriceAdjustments,
    }),
  show: ({ organizationId, priceAdjustmentId }: PriceAdjustmentScope) =>
    queryOptions({
      queryKey: priceAdjustmentQueryKeys.show({ organizationId, priceAdjustmentId }),
      queryFn: getPriceAdjustment,
      placeholderData: (): PriceAdjustmentResult | undefined => {
        const cachedPriceAdjustment = queryClient
          .getQueryData(
            priceAdjustmentQueries.index({ organizationId, filters: priceAdjustmentFilters })
              .queryKey
          )
          ?.find((adjustment) => adjustment.id === Number(priceAdjustmentId))

        // A list row carries no boundary of its own; the detail fetch brings it.
        return cachedPriceAdjustment
          ? { ...cachedPriceAdjustment, nextPriceAdjustmentChangeAt: null }
          : undefined
      },
    }),
  timeline: ({ organizationId }: PriceAdjustmentOrganizationScope) =>
    queryOptions({
      queryKey: priceAdjustmentQueryKeys.timeline({ organizationId }),
      queryFn: getPriceAdjustmentTimeline,
    }),
}

function useRefetchPriceAdjustmentsAtBoundary({
  organizationId,
  boundaryAt,
  dedupeKey,
}: {
  organizationId: number
  boundaryAt: string | null
  dedupeKey: string
}) {
  const refreshPriceAdjustments = useEffectEvent(async () => {
    if (!organizationId) {
      return
    }

    // Reject when the refetch fails so the boundary scheduler clears its dedupe and the next mount retries.
    await queryClient.invalidateQueries(
      { queryKey: priceAdjustmentQueryKeys.all({ organizationId }) },
      { throwOnError: true }
    )
  })

  useEffect(() => {
    if (!organizationId || !boundaryAt) {
      return
    }

    return schedulePriceAdjustmentBoundaryCallback({
      boundaryAt,
      onBoundary: refreshPriceAdjustments,
      dedupeKey: `price-adjustments:${dedupeKey}`,
    })
  }, [boundaryAt, dedupeKey, organizationId])
}

export function usePriceAdjustments({
  organizationId,
  filters = priceAdjustmentFilters,
  params = {},
}: PriceAdjustmentIndexScope & {
  params?: QueryParams<ReturnType<typeof priceAdjustmentQueries.index>>
} = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  const query = useSuspenseQuery({
    ...priceAdjustmentQueries.index({
      organizationId: resolvedOrganizationId,
      filters,
    }),
    ...params,
  })

  useRefetchPriceAdjustmentsAtBoundary({
    organizationId: resolvedOrganizationId,
    boundaryAt: getPriceAdjustmentRefreshBoundaryAt(query.data),
    dedupeKey: `index:${resolvedOrganizationId}`,
  })

  return query
}

/** The price change list for screens that still work when it fails to load. */
export function useOptionalPriceAdjustments({
  organizationId,
  filters = priceAdjustmentFilters,
  params = {},
}: PriceAdjustmentIndexScope & {
  params?: QueryParams<ReturnType<typeof priceAdjustmentQueries.index>>
} = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  const query = useQuery({
    ...priceAdjustmentQueries.index({
      organizationId: resolvedOrganizationId,
      filters,
    }),
    retry: false,
    throwOnError: false,
    ...params,
  })

  useRefetchPriceAdjustmentsAtBoundary({
    organizationId: resolvedOrganizationId,
    boundaryAt: getPriceAdjustmentRefreshBoundaryAt(query.data),
    dedupeKey: `optional-index:${resolvedOrganizationId}`,
  })

  return query
}

export function usePriceAdjustment(
  priceAdjustmentId: PriceAdjustmentIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof priceAdjustmentQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  const query = useSuspenseQuery({
    ...priceAdjustmentQueries.show({
      organizationId: resolvedOrganizationId,
      priceAdjustmentId,
    }),
    ...params,
  })

  useRefetchPriceAdjustmentsAtBoundary({
    organizationId: resolvedOrganizationId,
    boundaryAt: getPriceAdjustmentRefreshBoundaryAt(query.data),
    dedupeKey: `show:${resolvedOrganizationId}:${priceAdjustmentId}`,
  })

  return query
}

export function usePriceAdjustmentTimeline({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof priceAdjustmentQueries.timeline>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...priceAdjustmentQueries.timeline({
      organizationId: resolvedOrganizationId,
    }),
    ...params,
  })
}

/** Previews the change on one example product; off until there is a payload. */
export function usePriceAdjustmentPreview(
  payload: PriceAdjustmentPreviewInput | null | undefined,
  {
    organizationId,
    enabled = true,
    ...params
  }: OrganizationScope & PriceAdjustmentPreviewQueryParams = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    queryKey: priceAdjustmentQueryKeys.preview({
      organizationId: resolvedOrganizationId,
      payload,
    }),
    queryFn: payload
      ? () => previewPriceAdjustment({ organizationId: resolvedOrganizationId, ...payload })
      : skipToken,
    enabled: Boolean(enabled && payload),
    placeholderData: keepPreviousData,
    ...params,
  })
}

export function useCreatePriceAdjustmentMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: PriceAdjustmentInput) =>
      createPriceAdjustment({ organizationId, ...values }),
    onSuccess: () => {
      void invalidatePricingMutationDependencies(organizationId)
    },
  })
}

function cachePriceAdjustmentDetail(
  organizationId: number,
  priceAdjustment: SavedPriceAdjustmentResult | undefined,
  fallbackPriceAdjustmentId: PriceAdjustmentIdInput | undefined
) {
  if (!priceAdjustment) {
    return
  }

  // A permanent change rewrites prices and deletes the price change instead of saving it, so its
  // detail would only 404 now; drop it rather than keep showing the deleted change.
  if ('permanent' in priceAdjustment) {
    if (fallbackPriceAdjustmentId) {
      queryClient.removeQueries({
        queryKey: priceAdjustmentQueryKeys.show({
          organizationId,
          priceAdjustmentId: fallbackPriceAdjustmentId,
        }),
      })
    }

    return
  }

  const priceAdjustmentId = priceAdjustment.id ?? fallbackPriceAdjustmentId

  if (!priceAdjustmentId) {
    return
  }

  queryClient.setQueryData(
    priceAdjustmentQueryKeys.show({ organizationId, priceAdjustmentId }),
    priceAdjustment
  )
}

export function useUpdatePriceAdjustmentMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: PriceAdjustmentInput & { priceAdjustmentId: PriceAdjustmentIdInput }) =>
      updatePriceAdjustment({ organizationId, ...values }),
    onSuccess: (priceAdjustment, values) => {
      cachePriceAdjustmentDetail(organizationId, priceAdjustment, values?.priceAdjustmentId)
      void invalidatePricingMutationDependencies(organizationId)
    },
  })
}

export function useDisablePriceAdjustmentMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      priceAdjustmentId,
      mode,
    }: {
      priceAdjustmentId: PriceAdjustmentIdInput
      mode: PriceAdjustmentDisableMode
    }) => disablePriceAdjustment({ organizationId, priceAdjustmentId, mode }),
    onSuccess: (priceAdjustment, values) => {
      cachePriceAdjustmentDetail(organizationId, priceAdjustment, values?.priceAdjustmentId)
      void invalidatePricingMutationDependencies(organizationId)
    },
  })
}

export function useEnablePriceAdjustmentMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (priceAdjustmentId: PriceAdjustmentIdInput) =>
      enablePriceAdjustment({ organizationId, priceAdjustmentId }),
    onSuccess: (priceAdjustment, priceAdjustmentId) => {
      cachePriceAdjustmentDetail(organizationId, priceAdjustment, priceAdjustmentId)
      void invalidatePricingMutationDependencies(organizationId)
    },
  })
}

export function useResetActivePriceAdjustmentsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => resetActivePriceAdjustments({ organizationId }),
    onSuccess: () => {
      void invalidatePricingMutationDependencies(organizationId)
    },
  })
}

const cleanupDeletedPriceAdjustmentQueries = ({
  organizationId,
  priceAdjustmentId,
}: {
  organizationId: number
  priceAdjustmentId: PriceAdjustmentIdInput
}) => {
  queryClient.removeQueries({
    queryKey: priceAdjustmentQueryKeys.show({ organizationId, priceAdjustmentId }),
  })

  return invalidatePricingMutationDependencies(organizationId)
}

export function useDeletePriceAdjustmentMutation({
  cleanupOnSuccess = true,
}: {
  /** False for callers that clean up after navigating away (`cleanupDeletedPriceAdjustment`). */
  cleanupOnSuccess?: boolean
} = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedPriceAdjustment = (priceAdjustmentId: PriceAdjustmentIdInput) =>
    cleanupDeletedPriceAdjustmentQueries({ organizationId, priceAdjustmentId })

  const mutation = useMutation({
    mutationFn: (priceAdjustmentId: PriceAdjustmentIdInput) =>
      deletePriceAdjustment({ organizationId, priceAdjustmentId }),
    onSuccess: async (_, priceAdjustmentId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedPriceAdjustment(priceAdjustmentId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedPriceAdjustment,
  }
}
